import { AccountCircleOutlined } from '@mui/icons-material';
import { Grid, Skeleton } from '@mui/material';
import {
  formatDate,
  ReadOnlyField,
  Show,
  SummaryCard,
  useIsMobile,
} from '@side/pantry';
import { TrackOfferEvent } from 'src/modules/analytics/useTrackAnalytics/domains/offers/events';
import { useTrackOfferAnalytics } from 'src/modules/analytics/useTrackAnalytics/domains/offers/useTrackOfferAnalytics';
import { ClientGroupName } from '@/components/ClientGroupName/ClientGroupName';
import { getBuyerAgreementStatus } from '../../hooks/useBuyerAgreementStatus';
import type { ClientGroup } from '../../types';
import {
  AgreementStatusDisplay,
  formatCommission,
} from './BuyerInfoCard.helpers';
import { BuyerInfoCardDetailsLink } from './BuyerInfoCardDetailsLink';

interface Props {
  buyer?: Pick<
    ClientGroup,
    | 'id'
    | 'agreementStatus'
    | 'startDate'
    | 'endDate'
    | 'commissionType'
    | 'commissionValue'
    | 'contacts'
  > | null;
  alwaysLinkToDocuments?: boolean;
}

function BuyerInfoCardSkeleton() {
  const textSkeleton = (
    <Skeleton
      variant="text"
      width={45}
      sx={{ backgroundColor: 'grey.200' }}
      animation={false}
    />
  );

  return (
    <SummaryCard
      data-testid="buyer-info-card-skeleton"
      icon={<AccountCircleOutlined sx={{ color: 'action.disabled' }} />}
      title={
        <Skeleton
          variant="text"
          width={68}
          sx={{
            backgroundColor: 'grey.200',
          }}
          animation={false}
        />
      }
    >
      <Grid container spacing={4.5}>
        <Grid size={{ xs: 6, lg: 3 }}>
          <ReadOnlyField
            value={textSkeleton}
            label="Agreement Status"
            labelProps={{ color: 'text.disabled' }}
          />
        </Grid>
        <Grid size={{ xs: 6, lg: 3 }}>
          <ReadOnlyField
            value={textSkeleton}
            label="Start Date"
            labelProps={{ color: 'text.disabled' }}
          />
        </Grid>
        <Grid size={{ xs: 6, lg: 3 }}>
          <ReadOnlyField
            value={textSkeleton}
            label="End Date"
            labelProps={{ color: 'text.disabled' }}
          />
        </Grid>
        <Grid size={{ xs: 6, lg: 3 }}>
          <ReadOnlyField
            value={textSkeleton}
            label="Commission"
            labelProps={{ color: 'text.disabled' }}
          />
        </Grid>
      </Grid>
    </SummaryCard>
  );
}

export function BuyerInfoCard({ buyer, alwaysLinkToDocuments = false }: Props) {
  const isMobile = useIsMobile('lg');
  const trackOfferEvent = useTrackOfferAnalytics();

  if (!buyer) {
    return <BuyerInfoCardSkeleton />;
  }

  const status = getBuyerAgreementStatus(buyer);

  return (
    <SummaryCard
      icon={<AccountCircleOutlined sx={{ color: 'action.active' }} />}
      title={<ClientGroupName contacts={buyer.contacts} />}
      action={
        <Show when={!isMobile}>
          <BuyerInfoCardDetailsLink
            buyer={buyer}
            alwaysLinkToDocuments={alwaysLinkToDocuments}
            onClick={() => {
              trackOfferEvent(TrackOfferEvent.ClickBuyerDetailsLink, {
                clientGroupId: buyer.id,
              });
            }}
          />
        </Show>
      }
    >
      <Grid container spacing={4.5}>
        <Grid size={{ xs: 6, lg: 3 }}>
          <ReadOnlyField
            value={<AgreementStatusDisplay agreementStatus={status} />}
            label="Agreement Status"
          />
        </Grid>
        <Grid size={{ xs: 6, lg: 3 }}>
          <ReadOnlyField
            value={
              buyer.startDate ? formatDate(buyer.startDate, 'shortDate') : null
            }
            label="Start Date"
          />
        </Grid>
        <Grid size={{ xs: 6, lg: 3 }}>
          <ReadOnlyField
            value={
              buyer.endDate ? formatDate(buyer.endDate, 'shortDate') : null
            }
            label="End Date"
          />
        </Grid>
        <Grid size={{ xs: 6, lg: 3 }}>
          <ReadOnlyField value={formatCommission(buyer)} label="Commission" />
        </Grid>
      </Grid>
    </SummaryCard>
  );
}
